import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Users, BookOpen, Globe, Layers, Smartphone, Briefcase, Check } from "lucide-react";
import { api } from "@/lib/api";
import { HERO } from "@/constants/testIds";

const ICONS = { users: Users, "book-open": BookOpen, globe: Globe, layers: Layers, smartphone: Smartphone, briefcase: Briefcase, code: Sparkles };

export default function Home() {
  const [site, setSite] = React.useState(null);
  const [services, setServices] = React.useState([]);
  const [products, setProducts] = React.useState([]);

  React.useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const s = site || {};

  return (
    <div>
      {/* HERO */}
      <section className="hero-mesh relative overflow-hidden grain">
        <div className="container-wide py-24 md:py-32 grid gap-12 lg:grid-cols-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="badge-cyan mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--sky-cyan)]" />
              IT Consulting · Custom Software · Staffing
            </div>
            <h1 data-testid={HERO.headline} className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight text-[color:var(--sky-ink)]">
              {s.hero_title || "Enterprise IT Solutions, Engineered for Growth"}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[color:var(--sky-muted)] max-w-2xl leading-relaxed">
              {s.hero_subtitle || "Custom CRM, LMS, Web & Mobile Applications, plus expert IT staffing — delivered by Skyverra Consulting."}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid={HERO.primaryCta} className="btn-primary">
                {s.hero_cta || "Start a Project"} <ArrowRight size={16} />
              </Link>
              <Link to="/services" data-testid={HERO.secondaryCta} className="btn-outline">
                Explore services
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "50+", v: "Projects Delivered" },
                { k: "20+", v: "Enterprise Clients" },
                { k: "6", v: "Core Services" },
              ].map((x) => (
                <div key={x.v}>
                  <div className="font-display font-bold text-3xl md:text-4xl text-[color:var(--sky-navy)]">{x.k}</div>
                  <div className="text-xs mt-1 text-[color:var(--sky-muted)] tracking-tight">{x.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[color:var(--sky-cyan)]/20 blur-3xl rounded-full" />
              <div className="relative card-flat p-6 md:p-8">
                <div className="overline">What we do</div>
                <ul className="mt-5 space-y-4">
                  {["Customized CRM & LMS platforms","Enterprise web & mobile apps","IT staffing (contract & full-time)","End-to-end product consulting"].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="mt-1 inline-flex w-6 h-6 items-center justify-center rounded-sm bg-[color:var(--sky-navy)] text-white">
                        <Check size={13} />
                      </span>
                      <span className="text-sm text-[color:var(--sky-ink)] font-medium">{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-[color:var(--sky-border)]">
                  <div className="font-mono-plex text-[11px] text-[color:var(--sky-muted)]">FOUNDER</div>
                  <div className="mt-1 font-display font-bold">Krutika Shah</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section">
        <div className="container-wide">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="overline">Services</div>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-5xl tracking-tight max-w-3xl">Everything you need to build, staff, and scale.</h2>
            </div>
            <Link to="/services" className="btn-outline text-sm">View all services <ArrowUpRight size={16} /></Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 6).map((sv) => {
              const Icon = ICONS[sv.icon] || Sparkles;
              return (
                <div key={sv.id} className="card-flat p-7">
                  <div className="w-11 h-11 rounded-md bg-[color:var(--sky-navy)] text-white inline-flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl tracking-tight">{sv.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--sky-muted)] leading-relaxed">{sv.tagline}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY SKYVERRA */}
      <section className="section bg-white border-y border-[color:var(--sky-border)]">
        <div className="container-wide grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <div className="overline">Why Skyverra</div>
            <h2 className="mt-3 font-display font-bold text-3xl md:text-5xl tracking-tight">Senior teams. Fixed timelines. Enterprise standards.</h2>
            <p className="mt-6 text-[color:var(--sky-muted)] leading-relaxed max-w-xl">
              We partner with founders, CTOs, and heads of technology to deliver software that runs the
              business — not just the pitch deck. Every engagement is led by a senior architect, backed by
              transparent reporting and clear SLAs.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              {[
                { t: "Senior-led delivery", d: "Every project runs with a senior architect and lead engineer from day one." },
                { t: "Fixed-scope pricing", d: "Predictable budgets, no surprise invoices, transparent change control." },
                { t: "Modern engineering", d: "TypeScript, cloud-native, CI/CD, observability, and security by default." },
                { t: "Long-term support", d: "Post-launch SLAs, feature roadmaps, and dedicated account managers." },
              ].map((f) => (
                <div key={f.t}>
                  <div className="font-display font-bold text-[color:var(--sky-ink)]">{f.t}</div>
                  <div className="text-sm text-[color:var(--sky-muted)] mt-1">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-md overflow-hidden border border-[color:var(--sky-border)]">
              <img src="https://images.pexels.com/photos/3862154/pexels-photo-3862154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900" alt="Consulting team" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block card-flat p-5 max-w-xs">
              <div className="overline">Mission</div>
              <p className="mt-2 text-sm text-[color:var(--sky-ink)] font-medium leading-snug">{s.mission || "Turn complex business problems into elegant, scalable software."}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS TEASER */}
      {products.length > 0 && (
        <section className="section">
          <div className="container-wide">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <div className="overline">Products</div>
                <h2 className="mt-3 font-display font-bold text-3xl md:text-5xl tracking-tight max-w-3xl">Ready-to-deploy platforms, built on years of client work.</h2>
              </div>
              <Link to="/products" className="btn-outline text-sm">Explore products <ArrowUpRight size={16} /></Link>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {products.slice(0, 3).map((p) => (
                <div key={p.id} className="card-flat overflow-hidden">
                  {p.image_url && <div className="aspect-[16/10] overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>}
                  <div className="p-6">
                    <div className="overline text-[10px]">{p.category}</div>
                    <h3 className="mt-2 font-display font-bold text-xl">{p.name}</h3>
                    <p className="mt-2 text-sm text-[color:var(--sky-muted)]">{p.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section">
        <div className="container-wide">
          <div className="relative rounded-md overflow-hidden bg-[color:var(--sky-navy)] text-white p-10 md:p-16">
            <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[color:var(--sky-cyan)]/25 blur-3xl" />
            <div className="relative grid gap-10 lg:grid-cols-2 items-center">
              <div>
                <div className="overline text-white/70">Let&apos;s build</div>
                <h2 className="mt-3 font-display font-black text-3xl md:text-5xl leading-tight tracking-tight">Have a project or a staffing need? Start with a 30-minute discovery call.</h2>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <Link to="/contact" className="btn-primary bg-[color:var(--sky-cyan)] text-[#062133] hover:bg-white">Book a call <ArrowRight size={16} /></Link>
                <Link to="/careers" className="btn-outline border-white/50 text-white hover:bg-white hover:text-[color:var(--sky-navy)]">Join our team</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
